import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { useWalletStore, WalletStore } from 'store/index'
import {
  PROVIDER_ID,
  walletClients,
  WalletClient,
  walletProviders as providers
} from 'wallet/index'
import shallow from 'zustand/shallow'
import { TransactionsArray } from 'types/api'
import { formatBalance } from 'helpers/utilities'
import useErrorToast from './useErrorToast'

export { PROVIDER_ID }

export const walletProviders = Object.values(providers).map((walletInfo) => walletInfo)

const getWalletClient = async (id: PROVIDER_ID | undefined): Promise<WalletClient> => {
  if (!id) {
    throw new Error('No wallet provider id provided')
  }

  const client = await walletClients[id]

  if (!client) {
    throw new Error(`No wallet client found for provider id: ${id}`)
  }

  return client
}

const walletProviderSelector = (state: WalletStore) => ({
  allAccounts: state.accounts,
  activeAccount: state.activeAccount
})

export default function useWallet() {
  const { allAccounts, activeAccount } = useWalletStore(walletProviderSelector, shallow)
  const allAccountsDependency = JSON.stringify(allAccounts)
  const activeAccountDependency = JSON.stringify(activeAccount)
  const accounts = useMemo(
    () => allAccounts.filter((account) => account.providerId === activeAccount?.providerId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allAccountsDependency, activeAccountDependency]
  )

  const handleError = useErrorToast()

  const getBalance = async () => {
    if (!activeAccount) throw new Error('No selected account.')
    const walletClient = await getWalletClient(activeAccount?.providerId)

    return await walletClient?.getBalance(activeAccount.address)
  }

  const [walletBalance, setWalletBalance] = useState<string | null>(null)

  const { data: balance } = useQuery(['balance', activeAccount?.address], getBalance, {
    enabled: !!activeAccount?.address,
    refetchInterval: 30000
  })

  useEffect(() => {
    if (balance !== undefined) {
      if (formatBalance(balance) !== walletBalance) {
        setWalletBalance(formatBalance(balance))
      }
    } else {
      setWalletBalance('0')
    }
  }, [balance, walletBalance])

  const hasSufficientBalance = async (
    transactionCost: number,
    address?: string,
    isPurchase = true
  ) => {
    if (!activeAccount) throw new Error('No selected account.')
    const walletClient = await getWalletClient(activeAccount?.providerId)

    const result = await walletClient
      ?.getAccountInfo(address || activeAccount.address)
      .catch(handleError)

    if (!result) {
      return false
    }

    const { amount: balance, 'min-balance': minBalance } = result

    if (balance === undefined || minBalance === undefined) {
      throw new Error('Unable to get balance information.')
    }

    const newMinBalance = minBalance + (isPurchase ? 100000 : 0) // minBalance + 0.1 ALGO
    const balanceRequired = transactionCost + newMinBalance

    return balance >= balanceRequired
  }

  const getAssets = async () => {
    if (!activeAccount) throw new Error('No selected account.')
    const walletClient = await getWalletClient(activeAccount?.providerId)

    return await walletClient?.getAssets(activeAccount.address)
  }

  const signTransactions = async (transactions: string[]) => {
    const walletClient = await getWalletClient(activeAccount?.providerId)

    return await walletClient?.signTransactions(transactions)
  }

  const groupTransactionsBySender = async (transactions: TransactionsArray) => {
    const walletClient = await getWalletClient(activeAccount?.providerId)

    return walletClient?.groupTransactionsBySender(transactions)
  }

  const signEncodedTransactions = async (transactions: TransactionsArray) => {
    const walletClient = await getWalletClient(activeAccount?.providerId)

    return await walletClient?.signEncodedTransactions(transactions)
  }

  const sendRawTransactions = async (transactions: Uint8Array[]) => {
    const walletClient = await getWalletClient(activeAccount?.providerId)

    return await walletClient?.sendRawTransactions(transactions)
  }

  const getAddress = () => {
    return activeAccount?.address
  }

  return {
    accounts,
    activeAccount,
    getBalance,
    getAssets,
    signTransactions,
    signEncodedTransactions,
    sendRawTransactions,
    getAddress,
    walletBalance,
    hasSufficientBalance,
    groupTransactionsBySender
  }
}
